import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Copy, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { useAuth } from "../../lib/auth";

const who = (d) => [d.extraction?.prenom, d.extraction?.nom].filter(Boolean).join(" ");

function Pair({ p, onDone }) {
  const [keep, setKeep] = useState({});
  const [busy, setBusy] = useState(false);
  const act = async (path, msg) => {
    setBusy(true);
    try { await api.post(`/checks/duplicates/${path}`, { src: p.src.id, dst: p.dst.id, keep }); toast.success(msg); onDone(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Action impossible"); } finally { setBusy(false); }
  };
  const merge = () => window.confirm(`Fusionner la fiche email de ${who(p.src)} dans la fiche Chelly de ${who(p.dst)} ?`) && act("merge", "Fiches fusionnées");
  const key = `${p.src.id.slice(0, 8)}-${p.dst.id.slice(0, 8)}`;
  return (
    <div className="border border-norea-border rounded-md p-3" data-testid={`dup-pair-${key}`}>
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1 text-sm">
        <Link to={`/dossiers/${p.src.id}`} className="font-medium text-norea-primary hover:underline">{who(p.src)} <span className="text-xs text-norea-textMuted">(email)</span></Link>
        <span className="text-norea-textMuted">et</span>
        <Link to={`/dossiers/${p.dst.id}`} className="font-medium text-norea-primary hover:underline">{who(p.dst)} <span className="text-xs text-norea-textMuted">(Chelly n° {p.dst.chelly_id})</span></Link>
        <span className="text-xs text-norea-textMuted">· {p.why.join(", ")}</span>
      </div>
      {p.diffs.length > 0 && (
        <table className="w-full text-xs mt-2">
          <thead className="text-norea-textMuted"><tr><th className="text-left py-1 w-40">Écart</th><th className="text-left">Fiche Chelly (gardée par défaut)</th><th className="text-left">Fiche email</th></tr></thead>
          <tbody>
            {p.diffs.map((d) => (
              <tr key={d.key} className="border-t border-norea-border">
                <td className="py-1 text-norea-textMuted">{d.label}</td>
                {["dst", "src"].map((side) => (
                  <td key={side}>
                    <label className="inline-flex items-center gap-1.5 cursor-pointer">
                      <input type="radio" name={`${key}-${d.key}`} checked={(keep[d.key] || "dst") === side} onChange={() => setKeep({ ...keep, [d.key]: side })} data-testid={`dup-keep-${key}-${d.key}-${side}`} />
                      {String(d[side])}
                    </label>
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <div className="flex gap-2 mt-3">
        <button onClick={merge} disabled={busy} className="btn-primary text-xs disabled:opacity-50" data-testid={`dup-merge-${key}`}>{busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Copy className="w-3.5 h-3.5" />} Fusionner</button>
        <button onClick={() => act("dismiss", "Noté : 2 dossiers différents")} disabled={busy} className="btn-secondary text-xs" data-testid={`dup-dismiss-${key}`}>2 dossiers différents</button>
      </div>
    </div>
  );
}

export default function DuplicatesCard() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [items, setItems] = useState([]);
  const load = () => !isAdmin ? Promise.resolve() : api.get("/checks/duplicates").then((r) => setItems(r.data.items)).catch(() => {});
  useEffect(() => { load(); }, [isAdmin]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!isAdmin || !items.length) return null;
  return (
    <div className="card-flat p-5 mb-6" data-testid="duplicates-card">
      <h3 className="font-heading text-lg font-medium text-norea-text">Doublons à vérifier <span className="text-sm text-norea-textMuted">({items.length})</span></h3>
      <p className="text-xs text-norea-textMuted mt-1 mb-3">Fiche venue des emails qui ressemble à une fiche Chelly. « Fusionner » garde la fiche Chelly et y rattache tout (pièces, emails, historique) ; pour chaque écart, choisissez la valeur à garder.</p>
      <div className="space-y-3">{items.map((p) => <Pair key={p.src.id + p.dst.id} p={p} onDone={load} />)}</div>
    </div>
  );
}
